import React from 'react';
import { ItemValue, QuestionRadiogroupModel } from 'survey-core';
import { SurveyQuestionElementBase } from 'survey-react-ui';
import { Box, Button, Card, CardActions, CardContent, CardHeader, Grid, styled, Typography } from '@material-ui/core';
import { ComplianceLevels, MembershipTypes } from '../../types/enums';
import { Loading } from '@pec/aion-ui-components/components/Loading';

export const SELECTED_PRODUCT_ICON = 'orderPageSelectedProductIconSrc';
export const TYPE = 'mui-membership';

type MembershipDetails = {
  type: MembershipTypes;
  title: string;
  iconsSrc: string[];
  description: string;
  productId: string;
  price: string;
  redirectToSignup?: boolean;
};

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  minWidth: 280,
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: 'var(--gray)',
  border: '1px solid var(--gray-border)',
  borderRadius: '12px',
  '&:hover': {
    backgroundColor: 'var(--primary-04-)',
    cursor: 'pointer'
  },
  '&.selected': {
    backgroundColor: 'var(--primary-04-)',
    border: '1px solid var(--primary)'
  },
  '& .MuiCardHeader-root': {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    paddingBottom: theme.spacing(1),
    height: theme.spacing(23),
    flexDirection: 'column',
    '& .MuiCardHeader-avatar': {
      margin: theme.spacing(0, -2),
      width: 'calc(100% + 32px)'
    },
    '& .MuiTypography-root .MuiTypography-body2': {
      fontSize: '0.75rem',
      lineHeight: '1rem',
      letterSpacing: '0.025em',
      color: 'var(--black)'
    },
    '& .MuiTypography-root.MuiTypography-h5': {
      color: 'var(--black)'
    }
  },
  '& .MuiCardContent-root': {
    padding: theme.spacing(1, 0),
    '& .MuiTypography-root .MuiTypography-h5': {
      fontSize: '1,75rem',
      color: 'var(--black)',
      lineHeight: '2,25rem'
    },
    '& .MuiTypography-root.MuiTypography-caption:first-child': {
      marginRight: theme.spacing(1)
    }
  },
  '& .MuiListItem-root': {
    padding: 0,
    alignItems: 'start',
    '& .MuiListItemIcon-root': {
      minWidth: '30px'
    },
    '& .MuiListItemText-root': {
      margin: '0px',
      '&.excluded-option .MuiTypography-caption': {
        color: 'var(--light-gray)'
      }
    }
  },
  '& .MuiCardActions-root': {
    marginTop: 'auto',
    justifyContent: 'center',
    padding: theme.spacing(0.5, 3, 2)
  },
  '& .MuiCardActions-root .MuiButton-root': {
    border: `1px solid var(--light-outline)`,
    borderRadius: '100px',
    textTransform: 'unset',
    '&:disabled': {
      backgroundColor: 'var(--disabled)',
      borderColor: 'var(--disabled)'
    }
  }
}));

const StyledButton = styled(Button)(() => ({
  '&.MuiButton-root[aria-disabled="true"]': {
    borderColor: 'var(--disabled)',
    backgroundColor: 'var(--disabled)',
    color: 'var(--disabled-button)'
  }
}));

export class MuiMembershipModel extends QuestionRadiogroupModel {
  getType(): string {
    return TYPE;
  }

  get membershipDetails() {
    return this.getPropertyValue('membershipDetails');
  }

  set membershipDetails(value) {
    this.setPropertyValue('membershipDetails', value);
  }

  get strings() {
    return this.getPropertyValue('strings');
  }

  set strings(value) {
    this.setPropertyValue('strings', value);
  }
}

export class MuiMembership extends SurveyQuestionElementBase {
  buttonRef = React.createRef<any>();

  componentDidMount() {
    super.componentDidMount();
    this.question.questionRef = this.buttonRef;
  }

  get question() {
    return this.questionBase;
  }

  get choices() {
    return this.question.visibleChoices;
  }

  get strings() {
    return this.question.strings;
  }

  get details(): MembershipDetails[] {
    return this.question.membershipDetails;
  }

  handleClick(value: string, redirectToSignup?: boolean) {
    if (redirectToSignup) {
      window.open(`${window.location.origin}/signup?country=GB`, '_blank', 'noopener, noreferrer');
      return;
    }
    const choicesForRender = this.getChoicesForRender(this.choices, this.details);
    const choice = choicesForRender.find(item => item.productId === value);

    this.question.value = value;
    choice && sessionStorage.setItem(SELECTED_PRODUCT_ICON, choice.iconsSrc[0]);
  }

  getChoicesForRender(choices: ItemValue[], details: MembershipDetails[]) {
    return details.map(detail => {
      const choice = choices.find(item => {
        const normalizedText = item.text.replace('CHAS ', '');
        const isContractorOption = [
          ComplianceLevels.Elite,
          ComplianceLevels.Advanced,
          ComplianceLevels.Standard
        ].includes(item.text as ComplianceLevels);

        if (isContractorOption) {
          return detail.title === MembershipTypes.Contractor;
        }
        return normalizedText === detail.title;
      });
      if (choice) {
        return {
          ...detail,
          productId: choice.value,
          price:
            detail.type === MembershipTypes.Foundation
              ? detail.price
              : new Intl.NumberFormat(choice.originalItem.location, {
                  style: 'currency',
                  currency: choice.originalItem.currencyIso
                }).format(choice.originalItem.price),
          redirectToSignup: detail.redirectToSignup
        };
      }
      return detail;
    });
  }

  protected renderElement(): JSX.Element | null {
    const choicesForRender = this.getChoicesForRender(this.choices, this.details);
    const selectedValue = this.question.value ?? '';

    if (this.choices.length === 0) {
      return <Loading />;
    }

    return (
      <Grid style={{ marginTop: '32px', marginBottom: '4px', justifyContent: 'center' }} container spacing={3}>
        {choicesForRender.map((item, index) => (
          <Grid item xs={12} lg={4} key={item.type} data-itemsCount={choicesForRender.length}>
            <StyledCard
              className={`${item.productId === selectedValue ? 'selected' : ''}`}
              onClick={() => {
                this.handleClick(item.productId, item.redirectToSignup);
              }}
            >
              <CardHeader
                avatar={
                  <Box display="flex" justifyContent="center" mb={0.5}>
                    {item.iconsSrc.map(icon => (
                      <img
                        key={icon}
                        style={{ width: '72px', margin: '0 3px' }}
                        alt="membershipIcon"
                        src={`${process.env.PUBLIC_URL}${icon}`}
                      />
                    ))}
                  </Box>
                }
                title={
                  <Typography align="center" variant="h5" gutterBottom>
                    {item.title}
                  </Typography>
                }
                subheader={
                  <Typography align="center" variant="body2" gutterBottom>
                    {item.description}
                  </Typography>
                }
              ></CardHeader>
              <CardContent>
                {item.type === MembershipTypes.Foundation && (
                  <Typography align="center" variant="h5">
                    {item.price}
                  </Typography>
                )}
                {item.type === MembershipTypes.Contractor && (
                  <Box display="flex" justifyContent="center" alignItems="baseline">
                    <Typography variant="caption">{this.strings.startingFrom}</Typography>
                    <Typography variant="h5">{item.price}</Typography>
                    <Typography variant="caption">{this.strings.year}</Typography>
                  </Box>
                )}
                {item.type === MembershipTypes.Supplier && (
                  <Box display="flex" justifyContent="center" alignItems="baseline">
                    <Typography variant="h5">{item.price}</Typography>
                    <Typography variant="caption">{this.strings.year}</Typography>
                  </Box>
                )}
              </CardContent>
              <CardActions>
                <StyledButton
                  ref={index === 0 ? this.buttonRef : null}
                  aria-disabled={item.productId === selectedValue}
                  variant="outlined"
                  fullWidth
                  tabIndex={1}
                  color="primary"
                  onClick={event => {
                    this.handleClick(item.productId, item.redirectToSignup);
                    event.stopPropagation();
                  }}
                >
                  {item.productId === selectedValue ? this.strings.selected : this.strings.select}
                </StyledButton>
              </CardActions>
            </StyledCard>
          </Grid>
        ))}
      </Grid>
    );
  }
}
